import _ from 'lodash';
import * as util from '#packages/util';
import type { EditorAPI } from '#packages/editorAPI';
import type { CompRef } from '@wix/document-services-types';
import { newAddPanelAPI, castDesignCompType } from '#packages/newAddPanelAPI';
import isLocaleOverrideExists from '@wix/editor-add-panel/isLocaleOverrideExists';
import * as addPanelData from '#packages/addPanelData';
import addPanel from '#packages/oldAddPanel';

const applyExperiments = (sections: any): any => {
  return _.omit(sections, ['popup', 'boxSlideShow']);
};

type LanguageCode = keyof typeof Mapping;

function getLanguageCode(): LanguageCode | null {
  const languageCode = util.languages.getLanguageCode();
  if (
    languageCode &&
    languageCode.toLowerCase() !== 'en' &&
    isLocaleOverrideExists(languageCode)
  ) {
    return languageCode.toUpperCase() as LanguageCode;
  }
  return null;
}

const Mapping = {
  CS: () => import('@wix/editor-add-panel/sections/addPanelSectionsCS'),
  DA: () => import('@wix/editor-add-panel/sections/addPanelSectionsDA'),
  DE: () => import('@wix/editor-add-panel/sections/addPanelSectionsDE'),
  ES: () => import('@wix/editor-add-panel/sections/addPanelSectionsES'),
  FR: () => import('@wix/editor-add-panel/sections/addPanelSectionsFR'),
  HI: () => import('@wix/editor-add-panel/sections/addPanelSectionsHI'),
  IT: () => import('@wix/editor-add-panel/sections/addPanelSectionsIT'),
  JA: () => import('@wix/editor-add-panel/sections/addPanelSectionsJA'),
  KO: () => import('@wix/editor-add-panel/sections/addPanelSectionsKO'),
  NL: () => import('@wix/editor-add-panel/sections/addPanelSectionsNL'),
  NO: () => import('@wix/editor-add-panel/sections/addPanelSectionsNO'),
  PL: () => import('@wix/editor-add-panel/sections/addPanelSectionsPL'),
  PT: () => import('@wix/editor-add-panel/sections/addPanelSectionsPT'),
  RU: () => import('@wix/editor-add-panel/sections/addPanelSectionsRU'),
  SV: () => import('@wix/editor-add-panel/sections/addPanelSectionsSV'),
  TR: () => import('@wix/editor-add-panel/sections/addPanelSectionsTR'),
};

function getAddPanelDependencies() {
  const languageCode = getLanguageCode();
  return [
    languageCode
      ? Mapping[languageCode]().then((mod) => mod.default)
      : undefined,
  ] as const;
}

function loadAndCreate(editorAPI: EditorAPI) {
  return Promise.all(getAddPanelDependencies()).then(
    async ([optionalLanguage]) => {
      if (!util.addPanel.isNewPanelEnabled()) {
        if (optionalLanguage) {
          optionalLanguage = applyExperiments(optionalLanguage);
          addPanel.sections.applyTranslatedSections(optionalLanguage);
        }
      }

      function getCategory(categoryId: string) {
        return addPanelData.getCategory(editorAPI, categoryId);
      }

      function getSectionsByComponentType(
        compType: string,
        compRef: CompRef,
        withAdditionalBehaviors = false,
      ) {
        const componentType = castDesignCompType(compType, compRef, editorAPI);

        if (util.addPanel.isNewPanelEnabled()) {
          return newAddPanelAPI.getSectionsByComponentType(componentType);
        }
        return addPanelData.getSectionsByComponentType(
          componentType,
          withAdditionalBehaviors,
        );
      }

      function getDesignPanelTitleByComp(compType: string, compRef: CompRef) {
        const componentType = castDesignCompType(compType, compRef, editorAPI);
        if (util.addPanel.isNewPanelEnabled()) {
          return newAddPanelAPI.getDesignPanelTitleByComp(componentType);
        }
        return addPanelData.getDesignPanelTitleByComp(componentType);
      }

      function registerOverrideGroupsForCategory(
        categoryId: string,
        overrideFunction: AnyFixMe,
      ) {
        if (util.addPanel.isNewPanelEnabled()) {
          return;
        }
        addPanelData.manager.override.registerOverrideGroupsForCategory(
          categoryId,
          overrideFunction,
        );
      }

      function unRegisterOverrideGroupsForCategory(categoryId: string) {
        if (util.addPanel.isNewPanelEnabled()) {
          return;
        }
        addPanelData.manager.override.unRegisterOverrideGroupsForCategory(
          categoryId,
        );
      }

      function registerSectionToCategoryByGroup(
        categoryId: string,
        groupIndex: number,
        sectionIndex: number,
        sectionFunction: AnyFixMe,
      ) {
        if (util.addPanel.isNewPanelEnabled()) {
          return;
        }
        addPanelData.manager.registration.registerSectionToCategoryByGroup(
          categoryId,
          groupIndex,
          sectionIndex,
          sectionFunction,
        );
      }

      function registerSectionAtStart(
        categoryId: string,
        sectionFunction: AnyFixMe,
      ) {
        if (util.addPanel.isNewPanelEnabled()) {
          return;
        }
        addPanelData.manager.registration.registerSectionAtStart(
          categoryId,
          sectionFunction,
        );
      }

      function registerSectionAtEnd(
        categoryId: number,
        sectionFunction: AnyFixMe,
      ) {
        if (util.addPanel.isNewPanelEnabled()) {
          return;
        }
        addPanelData.manager.registration.registerSectionAtEnd(
          categoryId,
          sectionFunction,
        );
      }

      return {
        registerOverrideGroupsForCategory,
        unRegisterOverrideGroupsForCategory,
        registerSectionToCategoryByGroup,
        registerSectionAtStart,
        registerSectionAtEnd,
        getCategory,
        getSectionsByComponentType,
        getDesignPanelTitleByComp,
      };
    },
  );
}

export { loadAndCreate };
